import type { EnhancedSectionType } from '@/components/Section/Section';
import type { Seo } from '@/contentful/contentful-exports';
import { parseSolarByStatePath } from '@/utils/parseSolarByStatePath';

import { extractPhoneFromSections } from './extractPhoneFromSections';
import {
  type AreaServed,
  applyGeoLocationOverrides,
  buildAreaServed,
  buildServiceLocationLabel,
  resolveGeoPageUrl,
  SUNRUN_ORGANIZATION,
} from './geoSchemaShared';

export interface LocalBusinessSchemaData {
  '@context': 'https://schema.org';
  '@type': 'HomeAndConstructionBusiness';
  '@id': string;
  name: string;
  url: string;
  areaServed: AreaServed;
  parentOrganization: typeof SUNRUN_ORGANIZATION;
  telephone?: string;
}

export interface BuildLocalBusinessSchemaParams {
  path: string;
  seoFields?: Seo;
  sections: EnhancedSectionType[];
}

export function buildLocalBusinessSchema({
  path,
  seoFields,
  sections,
}: BuildLocalBusinessSchemaParams): LocalBusinessSchemaData | null {
  const metaTitle = seoFields?.fields?.metaTitle;
  const parsed = parseSolarByStatePath(path, metaTitle);

  if (parsed?.pageType !== 'branch') {
    return null;
  }

  const resolved = applyGeoLocationOverrides(parsed, seoFields);
  const location = buildServiceLocationLabel(resolved);
  const areaServed = buildAreaServed(resolved);
  if (!location || !areaServed) {
    return null;
  }

  const url = resolveGeoPageUrl(path, seoFields);

  const schema: LocalBusinessSchemaData = {
    '@context': 'https://schema.org',
    '@type': 'HomeAndConstructionBusiness',
    '@id': url,
    name: `Sunrun — ${location}`,
    url,
    areaServed,
    parentOrganization: SUNRUN_ORGANIZATION,
  };

  const telephone = extractPhoneFromSections(sections);
  if (telephone) {
    schema.telephone = telephone;
  }

  return schema;
}
